import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Icon } from "@iconify/react";
import { Formik } from "formik";
import * as yup from "yup";
import {
  FormikInput,
  FormikSelect,
  FormikSearchSelect,
  FormikTextArea,
  FormikDate,
} from "../../components/formik-components";
import Common from "../../helper/common";
import { respEnum, OpenMatchTypeEnum } from "../../constants/appConstant";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import DialogModal from "../../components/partials/modal/dialogModal";

const CreateMatch = () => {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [sports, setSports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [phoneCodes] = useState([
    { value: "+92", label: "+92 (PK)" },
    { value: "+1", label: "+1 (US)" },
    { value: "+44", label: "+44 (UK)" },
  ]);

  const getLocalDateTime = (offsetHours = 0) => {
    const now = new Date();
    now.setHours(now.getHours() + offsetHours);

    // Round to nearest :00, :30, or :59
    const minutes = now.getMinutes();
    let roundedMinutes = 0;

    if (minutes < 15) {
      // Round down to :00
      roundedMinutes = 0;
    } else if (minutes < 45) {
      // Round to :30
      roundedMinutes = 30;
    } else {
      // Round up to next hour :00, or if it's 23:xx, allow 23:59
      const hours = now.getHours();
      if (hours === 23 && minutes >= 45) {
        // Allow 23:59 specifically
        roundedMinutes = 59;
      } else {
        // Round up to next hour :00
        roundedMinutes = 0;
        now.setHours(hours + 1);
        if (now.getHours() === 0) {
          now.setDate(now.getDate() + 1);
        }
      }
    }

    now.setMinutes(roundedMinutes);
    now.setSeconds(0);
    now.setMilliseconds(0);

    const offsetMs = now.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(now.getTime() - offsetMs);
    return localDate.toISOString().slice(0, 16);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [clubsRes, sportsRes, categoriesRes] = await Promise.all([
        Common.ApiService.getInstance().request("GetOpenMatchClubsDDL", {}, "GET", false),
        Common.ApiService.getInstance().request("GetSports?isactive=true", {}, "GET", false),
        Common.ApiService.getInstance().request("GetOpenMatchCategoriesDDL", {}, "GET", false),
      ]);

      if (clubsRes?.status === respEnum.Success && clubsRes.data) {
        const clubsData = Array.isArray(clubsRes.data) ? clubsRes.data : [];
        setClubs(clubsData.map((c) => ({ id: c.id, name: c.name })));
      } else if (clubsRes?.status !== respEnum.Success) {
        toast.error(clubsRes?.message || "Failed to load clubs");
      }

      if (sportsRes?.status === respEnum.Success && sportsRes.data) {
        const sportsData = Array.isArray(sportsRes.data) ? sportsRes.data : [];
        setSports(sportsData.map((s) => ({ id: s.id, name: s.name })));
      } else if (sportsRes?.status !== respEnum.Success) {
        toast.error(sportsRes?.message || "Failed to load sports");
      }

      if (categoriesRes?.status === respEnum.Success && categoriesRes.data) {
        const categoriesData = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
        setCategories(categoriesData.map((c) => ({ id: c.id, name: c.name })));
        console.log("Categories loaded:", categoriesData.length, "items");
      } else {
        console.error("Failed to load categories:", categoriesRes);
        setCategories([]); // Set empty array to ensure dropdown still renders
        if (categoriesRes?.status !== respEnum.Success) {
          console.warn("Categories API error:", categoriesRes?.message);
        }
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load clubs, sports, or categories");
    } finally {
      setLoading(false);
    }
  };

  const lookupUserByPhone = async (phoneCode, phone) => {
    try {
      // Use new API that returns firstName, lastName, photo, and level (generic, default sportId = 1 for Padel)
      const response = await Common.ApiService.getInstance().request(
        `CheckUserExistWithLevel?phone=${encodeURIComponent(phone)}&sportId=1`,
        null,
        "GET",
        false
      );
      return response?.status === respEnum.Success ? response.data : null;
    } catch (error) {
      console.error("Error looking up user:", error);
      return null;
    }
  };

  const validationSchema = yup.object().shape({
    openmatchclub_id: yup.number().required("Club is required"),
    sport_id: yup.number().required("Sport is required"),
    cat_id: yup.number().when("type", {
      is: (type) => Number(type) !== OpenMatchTypeEnum.Exclusive,
      then: (schema) => schema.required("Category is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    start_datetime: yup.date().required("Start date & time is required"),
    end_datetime: yup.date().required("End date & time is required"),
    court_charges: yup.number().when("type", {
      is: (type) => Number(type) !== OpenMatchTypeEnum.Exclusive,
      then: (schema) => schema.required("Court charges are required").min(0, "Charges must be positive"),
      otherwise: (schema) => schema.nullable().min(0, "Charges must be positive"),
    }),
    notes: yup.string().nullable(),
    required_level: yup.number().when("type", {
      is: (type) => Number(type) !== OpenMatchTypeEnum.Exclusive,
      then: (schema) => schema.required("Required level is required").min(0).max(8),
      otherwise: (schema) => schema.nullable().min(0).max(8),
    }),
    playerCount: yup
      .number()
      .required("Player count is required")
      .min(2, "Minimum 2 players required")
      .max(20, "Maximum 20 players allowed")
      .test("is-even", "Player count must be even", (value) => value % 2 === 0),
    type: yup.number().required("Match type is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      // Validate at least 1 player is required for all match types
      if (!values.players || values.players.length === 0 && values.type===OpenMatchTypeEnum.Open) {
        toast.error("At least 1 player is required to create a match");
        setLoading(false);
        setSubmitting(false);
        return;
      }

      // Validate Exclusive type requirements
      if (values.type === OpenMatchTypeEnum.Exclusive) {
        // Exclusive type
        if (values.players.length !== values.playerCount) {
          toast.error(`Exclusive matches require exactly ${values.playerCount} players`);
          setLoading(false);
          setSubmitting(false);
          return;
        }

        // Validate all players have phone lookup
        const invalidPlayers = values.players.filter(p => !p.playerId);
        if (invalidPlayers.length > 0) {
          toast.error("Please verify all player phone numbers");
          setLoading(false);
          setSubmitting(false);
          return;
        }

        // Validate result is provided for Exclusive
        if (!values.side || !values.resultLogs || values.resultLogs.length === 0) {
          toast.error("Result is required for Exclusive matches");
          setLoading(false);
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        openmatchclub_id: typeof values.openmatchclub_id === 'object' && values.openmatchclub_id?.id
          ? values.openmatchclub_id.id
          : (typeof values.openmatchclub_id === 'number' ? values.openmatchclub_id : Number(values.openmatchclub_id)),
        sport_id: typeof values.sport_id === 'object' && values.sport_id?.id
          ? values.sport_id.id
          : (typeof values.sport_id === 'number' ? values.sport_id : Number(values.sport_id)),
        cat_id: values.cat_id && (typeof values.cat_id === 'object' && values.cat_id?.id
          ? values.cat_id.id
          : (typeof values.cat_id === 'number' ? values.cat_id : Number(values.cat_id))) || null,
        start_datetime: values.start_datetime,
        end_datetime: values.end_datetime,
        court_charges: values.type === OpenMatchTypeEnum.Exclusive ? null : (values.court_charges || 0),
        notes: values.notes,
        required_level: values.type === OpenMatchTypeEnum.Exclusive ? null : values.required_level,
        playerCount: values.playerCount,
        type: values.type,
        players: values.players?.map(p => ({
          phoneCode: p.phoneCode,
          phone: p.phone,
          side: p.side,
          position: p.position,
        })),
        side: values.side,
        resultLogs: values.resultLogs?.map(r => ({
          playerId: r.playerId,
          round: r.round,
          points: r.points,
          resultType: r.resultType,
          gameType: r.gameType,
          side: r.side,
        })),
      };

      const response = await Common.ApiService.getInstance().request(
        "CustomCreateOpenMatch",
        payload,
        "POST",
        false
      );

      if (response?.status === respEnum.Success) {
        toast.success("Open match created successfully!");
        navigate("/app/openmatches/manager");
      } else {
        toast.error(response?.message || "Failed to create match");
      }
    } catch (error) {
      console.error("Error creating match:", error);
      toast.error("An error occurred while creating the match");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Time rounding function
  const roundTime = (datetimeValue) => {
    if (!datetimeValue) return datetimeValue;
    const dateTime = new Date(datetimeValue);
    const hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();

    // Check if it's 23:59 - allow it
    if (hours === 23 && minutes === 59) {
      return datetimeValue;
    }

    // Round to nearest :00 or :30
    let roundedMinutes = minutes < 15 ? 0 : minutes < 45 ? 30 : 0;
    if (roundedMinutes === 0 && minutes >= 45) {
      dateTime.setHours(hours + 1);
      if (dateTime.getHours() === 0) {
        dateTime.setDate(dateTime.getDate() + 1);
      }
    }
    dateTime.setMinutes(roundedMinutes);
    dateTime.setSeconds(0);
    dateTime.setMilliseconds(0);

    // Convert back to datetime-local format
    const year = dateTime.getFullYear();
    const month = String(dateTime.getMonth() + 1).padStart(2, '0');
    const day = String(dateTime.getDate()).padStart(2, '0');
    const hour = String(dateTime.getHours()).padStart(2, '0');
    const minute = String(dateTime.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hour}:${minute}`;
  };

  // Time validation function
  const validateTime = (datetimeValue) => {
    if (!datetimeValue) return true;
    const dateTime = new Date(datetimeValue);
    const hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();

    // Allow 23:59
    if (hours === 23 && minutes === 59) return true;

    // Must be :00 or :30
    return minutes === 0 || minutes === 30;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <Card className="border-0 shadow-2xl bg-white overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-[#0C1B3A] via-[#1a2a4e] to-[#2E55B9] text-white py-8 px-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
                <Icon icon="heroicons:plus-circle" className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-white">Create Open Match</CardTitle>
                <p className="text-blue-200 mt-1">Set up a new match and invite players to join</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <Formik
              initialValues={{
                openmatchclub_id: "",
                sport_id: 1, // Default to Padel (SportTypeEnum.Padel_Tennis = 1)
                cat_id: null,
                start_datetime: getLocalDateTime(0),
                end_datetime: getLocalDateTime(1), // 1 hour later
                court_charges: "",
                notes: "",
                required_level: null, // Required for Open matches, null for Exclusive
                playerCount: 4,
                type: OpenMatchTypeEnum.Exclusive,
                players: [],
                side: "",
                resultLogs: [],
              }}
              validationSchema={validationSchema}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={handleSubmit}
            >
              {(formikProps) => (
                <form onSubmit={formikProps.handleSubmit} className="space-y-8">
                  {/* Basic Match Information */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                      <Icon icon="heroicons:information-circle" className="w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Match Information</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Icon icon="heroicons:building-storefront" className="w-4 h-4 text-gray-400" />
                          Club <span className="text-red-500">*</span>
                        </label>
                        <FormikSearchSelect
                          formikProps={formikProps}
                          name="openmatchclub_id"
                          data={clubs}
                          valueKey="id"
                          labelKey="name"
                          placeholder="Select Club"
                          label=""
                        />
                      </div>

                      <div className="space-y-2 hidden">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Icon icon="heroicons:fire" className="w-4 h-4 text-gray-400" />
                          Sport <span className="text-red-500">*</span>
                        </label>
                        <FormikSearchSelect
                          formikProps={formikProps}
                          name="sport_id"
                          data={sports}
                          valueKey="id"
                          labelKey="name"
                          placeholder="Select Sport"
                          label=""
                          disabled={formikProps.values.sport_id === 1} // Disable if Padel is selected
                        />
                      </div>


                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Icon icon="heroicons:tag" className="w-4 h-4 text-gray-400" />
                          Match Type <span className="text-red-500">*</span>
                        </label>
                        <FormikSelect
                          formikProps={formikProps}
                          name="type"
                          data={[
                            { value: OpenMatchTypeEnum.Open, label: "Open" },
                            { value: OpenMatchTypeEnum.Exclusive, label: "Exclusive" },
                          ]}
                          valueKey="value"
                          labelKey="label"
                          placeholder="Select Type"
                          onChange={(value) => {
                            // value is already converted to number by FormikSelect
                            formikProps.setFieldValue("type", value);
                            if (Number(value) === OpenMatchTypeEnum.Open) {
                              formikProps.setFieldValue("side", "");
                              formikProps.setFieldValue("resultLogs", []);
                            } else if (Number(value) === OpenMatchTypeEnum.Exclusive) {
                              // Clear cat_id and court_charges for Exclusive type
                              formikProps.setFieldValue("cat_id", null);
                              formikProps.setFieldValue("court_charges", "");
                            }
                          }}
                          className="w-full"
                        />
                      </div>


                      {Number(formikProps.values.type) !== OpenMatchTypeEnum.Exclusive && (
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <Icon icon="heroicons:tag" className="w-4 h-4 text-gray-400" />
                            Category <span className="text-red-500">*</span>
                          </label>
                          <FormikSearchSelect
                            formikProps={formikProps}
                            name="cat_id"
                            data={categories || []}
                            valueKey="id"
                            labelKey="name"
                            placeholder={categories.length === 0 ? "No categories available" : "Select Category"}
                            label=""
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Icon icon="heroicons:calendar" className="w-4 h-4 text-gray-400" />
                          Start Date & Time <span className="text-red-500">*</span>
                        </label>
                        <FormikDate
                          formikProps={formikProps}
                          name="start_datetime"
                          type="datetime-local"
                          className="w-full"
                          onBlur={(e) => {
                            if (e.target.value) {
                              const rounded = roundTime(e.target.value);
                              if (rounded !== e.target.value) {
                                formikProps.setFieldValue("start_datetime", rounded);
                              }
                              if (!validateTime(rounded)) {
                                Common.Utility.handleBlur(rounded.split('T')[1], "Start Time", true);
                              }
                            }
                          }}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Icon icon="heroicons:calendar" className="w-4 h-4 text-gray-400" />
                          End Date & Time <span className="text-red-500">*</span>
                        </label>
                        <FormikDate
                          formikProps={formikProps}
                          name="end_datetime"
                          type="datetime-local"
                          min={formikProps.values.start_datetime || undefined}
                          className="w-full"
                          onBlur={(e) => {
                            if (e.target.value) {
                              const rounded = roundTime(e.target.value);
                              if (rounded !== e.target.value) {
                                formikProps.setFieldValue("end_datetime", rounded);
                              }
                              if (!validateTime(rounded)) {
                                Common.Utility.handleBlur(rounded.split('T')[1], "End Time", true);
                              }
                            }
                          }}
                        />
                      </div>

                      {Number(formikProps.values.type) !== OpenMatchTypeEnum.Exclusive && (
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <Icon icon="heroicons:currency-dollar" className="w-4 h-4 text-gray-400" />
                            Court Charges <span className="text-red-500">*</span>
                          </label>
                          <FormikInput
                            formikProps={formikProps}
                            name="court_charges"
                            type="number"
                            placeholder="Enter court charges"
                            className="w-full"
                          />
                        </div>
                      )}

                      <div className="space-y-2 hidden">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Icon icon="heroicons:users" className="w-4 h-4 text-gray-400" />
                          Player Count <span className="text-red-500">*</span>
                        </label>
                        <FormikInput
                          formikProps={formikProps}
                          name="playerCount"
                          type="number"
                          placeholder="e.g., 4, 6, 8"
                          min="2"
                          max="20"
                          step="2"
                          className="w-full"
                          disabled={true}
                        />
                        <p className="text-xs text-gray-500 ml-1">Must be even number (2, 4, 6, 8...)</p>
                      </div>

                      {Number(formikProps.values.type) !== OpenMatchTypeEnum.Exclusive && (
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <Icon icon="heroicons:chart-bar" className="w-4 h-4 text-gray-400" />
                            Required Level <span className="text-red-500">*</span>
                          </label>
                          <FormikInput
                            formikProps={formikProps}
                            name="required_level"
                            type="number"
                            placeholder="e.g., 5.0"
                            min="0"
                            max="8"
                            step="0.1"
                            className="w-full"
                          />
                        </div>
                      )}
                    </div>

                    {Number(formikProps.values.type) !== OpenMatchTypeEnum.Exclusive && (
                      <div className="space-y-2" hidden>
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Icon icon="heroicons:document-text" className="w-4 h-4 text-gray-400" />
                          Notes (Optional)
                        </label>
                        <FormikTextArea
                          formikProps={formikProps}
                          name="notes"
                          placeholder="Add any additional information about the match..."
                          rows={4}
                          className="w-full resize-none"
                        />
                      </div>
                    )}

                    {/* Players Section */}
                    <div className="pt-6 border-t border-gray-100">
                      <PlayerManagementSection
                        formikProps={formikProps}
                        phoneCodes={phoneCodes}
                        lookupUserByPhone={lookupUserByPhone}
                      />
                    </div>

                    {/* Result Section for Exclusive */}
                    {Number(formikProps.values.type) === OpenMatchTypeEnum.Exclusive && (
                      <div className="pt-6 border-t border-gray-100">
                        <ResultUploadSection formikProps={formikProps} />
                      </div>
                    )}

                    <div className="flex gap-4 justify-end pt-6 border-t border-gray-100">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate("/app/openmatches/manager")}
                        disabled={formikProps.isSubmitting || loading}
                        className="px-6"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={formikProps.isSubmitting || loading}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-8 shadow-lg shadow-blue-600/20 transition-all"
                      >
                        {loading ? (
                          <>
                            <Icon icon="eos-icons:loading" className="w-4 h-4 mr-2" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Icon icon="heroicons:check" className="w-4 h-4 mr-2" />
                            Create Match
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const PlayerManagementSection = ({ formikProps, phoneCodes, lookupUserByPhone }) => {
  const [lookupLoading, setLookupLoading] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null); // { side, position }
  const [modalPhoneCode, setModalPhoneCode] = useState("+92");
  const [modalPhone, setModalPhone] = useState("");
  const [modalPlayerData, setModalPlayerData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const phoneInputRef = React.useRef(null);
  const prevPlayerCountRef = React.useRef(formikProps.values.playerCount || 4);

  // Handle playerCount changes - remove players that exceed new limits
  useEffect(() => {
    const currentPlayers = formikProps.values.players || [];
    const playerCount = formikProps.values.playerCount || 4;
    const prevPlayerCount = prevPlayerCountRef.current;

    // Only process if playerCount actually changed
    if (playerCount === prevPlayerCount || !playerCount || playerCount < 2) {
      return;
    }

    prevPlayerCountRef.current = playerCount;
    const maxPlayersPerSide = playerCount / 2;

    // Remove players that exceed the new position limits
    const validPlayers = currentPlayers.filter(p => {
      if (p.side === "A" || p.side === "B") {
        return p.position <= maxPlayersPerSide;
      }
      return false;
    });

    if (validPlayers.length !== currentPlayers.length) {
      formikProps.setFieldValue("players", validPlayers, false);
    }
  }, [formikProps.values.playerCount]);

  const openAddPlayerModal = (side, position) => {
    setSelectedSlot({ side, position });
    setModalPhoneCode("+92");
    setModalPhone("");
    setModalPlayerData(null);
    setModalOpen(true);
  };

  // Focus phone input when modal opens
  useEffect(() => {
    if (modalOpen && phoneInputRef.current) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        phoneInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [modalOpen]);

  const handleModalPhoneLookup = useCallback(async () => {
    if (!modalPhone || modalPhone.length < 10) {
      setModalPlayerData(null);
      return;
    }

    setModalLoading(true);
    try {
      const user = await lookupUserByPhone(modalPhoneCode, modalPhone);
      if (user) {
        // Use padelLevel directly from API response
        setModalPlayerData({
          id: user.id,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          image: user.image || null,
          level: user.level || null, // Generic level field
          phoneCode: user.phoneCode || modalPhoneCode,
          phone: user.phone || modalPhone,
        });
        toast.success("Player found!");
      } else {
        setModalPlayerData(null);
        // toast.error("Player not found in PlayPro");
      }
    } catch (error) {
      console.error("Error looking up player:", error);
      toast.error("Error looking up player");
      setModalPlayerData(null);
    } finally {
      setModalLoading(false);
    }
  }, [modalPhone, modalPhoneCode, lookupUserByPhone]);

  const addPlayerToSlot = useCallback(() => {
    if (!selectedSlot || !modalPlayerData) {
      toast.error("Please search and verify player first");
      return;
    }

    const players = [...formikProps.values.players];
    const playerIndex = players.findIndex(p => p.side === selectedSlot.side && p.position === selectedSlot.position);

    const playerData = {
      phoneCode: modalPhoneCode,
      phone: modalPhone,
      side: selectedSlot.side,
      position: selectedSlot.position,
      playerId: modalPlayerData.id,
      playerName: `${modalPlayerData.firstName} ${modalPlayerData.lastName}`.trim(),
      playerImage: modalPlayerData.image,
      playerLevel: modalPlayerData.level,
      firstName: modalPlayerData.firstName,
      lastName: modalPlayerData.lastName,
      isValid: true,
    };

    if (playerIndex >= 0) {
      players[playerIndex] = playerData;
    } else {
      players.push(playerData);
    }

    formikProps.setFieldValue("players", players);
    setModalOpen(false);
    setModalPlayerData(null);
    setModalPhone("");
    toast.success("Player added successfully!");
  }, [selectedSlot, modalPlayerData, modalPhoneCode, modalPhone, formikProps]);

  const removePlayerFromSlot = (side, position) => {
    const players = [...formikProps.values.players];
    const playerIndex = players.findIndex(p => p.side === side && p.position === position);
    if (playerIndex >= 0) {
      players.splice(playerIndex, 1);
      formikProps.setFieldValue("players", players);
    }
  };

  const maxPlayersPerSide = (formikProps.values.playerCount || 4) / 2;
  const slots = Array.from({ length: maxPlayersPerSide }, (_, i) => i + 1);
  const players = formikProps.values.players || [];

  const getPlayerForSlot = (side, position) => {
    return players.find(p => p.side === side && p.position === position);
  };

  // Modal content component
  const ModalContent = useCallback(() => {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#0C1B3A' }}>
              Code
            </label>
            <select
              value={modalPhoneCode}
              onChange={(e) => {
                setModalPhoneCode(e.target.value);
                setModalPlayerData(null);
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:outline-none transition-all"
              style={{
                borderColor: '#d1d5db',
                color: '#0C1B3A'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2E55B9';
                e.target.style.boxShadow = '0 0 0 2px rgba(46, 85, 185, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            >
              {phoneCodes.map((code) => (
                <option key={code.value} value={code.value}>
                  {code.label.split(" ")[0]}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#0C1B3A' }}>
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                ref={phoneInputRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={modalPhone}
                onChange={(e) => {
                  // Only allow numbers
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setModalPhone(value);
                  setModalPlayerData(null);
                  // Maintain focus after state update
                  requestAnimationFrame(() => {
                    if (phoneInputRef.current) {
                      phoneInputRef.current.focus();
                    }
                  });
                }}
                onKeyDown={(e) => {
                  // Search on Enter key
                  if (e.key === 'Enter' && modalPhone && modalPhone.length >= 10) {
                    e.preventDefault();
                    handleModalPhoneLookup();
                  }
                }}
                placeholder="Enter phone number"
                className="w-full px-3 py-2 pr-10 border rounded-lg text-sm focus:outline-none transition-all"
                style={{
                  borderColor: '#d1d5db',
                  color: '#0C1B3A'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2E55B9';
                  e.target.style.boxShadow = '0 0 0 2px rgba(46, 85, 185, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                autoComplete="tel"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {modalLoading ? (
                  <Icon
                    icon="eos-icons:loading"
                    className="w-5 h-5 animate-spin"
                    style={{ color: '#2E55B9' }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (modalPhone && modalPhone.length >= 10) {
                        handleModalPhoneLookup();
                      } else {
                        toast.error("Please enter a valid phone number (at least 10 digits)");
                      }
                    }}
                    className="p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                    style={{ color: '#2E55B9' }}
                    title="Search Player"
                  >
                    <Icon icon="heroicons:magnifying-glass" className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Player Details */}
        {modalPlayerData && (
          <div className="p-4 rounded-xl border" style={{ backgroundColor: 'rgba(46, 85, 185, 0.05)', borderColor: '#2E55B9' }}>
            <div className="flex items-center gap-4">
              {modalPlayerData.image ? (
                <img
                  src={Common.Utility.getImagePath(modalPlayerData.image)}
                  alt={`${modalPlayerData.firstName} ${modalPlayerData.lastName}`}
                  className="w-16 h-16 object-cover rounded-full border-2"
                  style={{ borderColor: '#2E55B9' }}
                />
              ) : (
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0C1B3A 0%, #2E55B9 100%)' }}>
                  <Icon icon="heroicons:user" className="w-8 h-8 text-white" />
                </div>
              )}
              <div className="flex-1">
                <div className="font-semibold text-lg" style={{ color: '#0C1B3A' }}>
                  {modalPlayerData.firstName} {modalPlayerData.lastName}
                </div>
                <div className="text-sm mt-1" style={{ color: '#6b7280' }}>
                  {modalPlayerData.phoneCode} {modalPlayerData.phone}
                </div>
                {modalPlayerData.level !== null && (
                  <div className="mt-2">
                    <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                      Level: {modalPlayerData.level?.toFixed(2) || "N/A"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [modalPhoneCode, modalPhone, modalPlayerData, modalLoading, phoneCodes, handleModalPhoneLookup]);

  const ModalFooter = useCallback(() => (
    <div className="flex gap-3 w-full">
      <Button
        type="button"
        onClick={() => {
          setModalOpen(false);
          setModalPlayerData(null);
          setModalPhone("");
        }}
        className="flex-1"
        style={{
          backgroundColor: 'transparent',
          border: '2px solid #2E55B9',
          color: '#2E55B9'
        }}
      >
        Cancel
      </Button>
      <Button
        type="button"
        onClick={addPlayerToSlot}
        disabled={!modalPlayerData}
        className="flex-1 text-white"
        style={{
          backgroundColor: modalPlayerData ? '#2E55B9' : '#9ca3af',
          border: 'none'
        }}
      >
        Add Player
      </Button>
    </div>
  ), [modalPlayerData, addPlayerToSlot]);


  return (
    <>
      <div className="space-y-6">
        {/* Section Header */}
        <div className="bg-gradient-to-r from-[#0C1B3A] to-[#2E55B9] rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Icon icon="heroicons:users" className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Player Management</h3>
                <p className="text-blue-200 text-sm">Add players to Side A and Side B</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {Number(formikProps.values.type) === OpenMatchTypeEnum.Exclusive && (
                <span className="px-3 py-1.5 bg-red-500/20 text-red-100 text-sm font-medium rounded-full border border-red-400/30">
                  <Icon icon="heroicons:exclamation-triangle" className="w-4 h-4 inline mr-1" />
                  Required for Exclusive
                </span>
              )}
              <span className="px-4 py-2 bg-white/20 text-white text-sm font-semibold rounded-full">
                {players.filter(p => p.isValid).length}/{formikProps.values.playerCount || 4} Players
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Side A */}
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 rounded-2xl shadow-lg shadow-blue-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-white font-black text-lg">A</span>
                  </div>
                  <div>
                    <p className="font-bold text-white">Side A</p>
                    <p className="text-blue-200 text-xs">Team One</p>
                  </div>
                </div>
                <span className="px-3 py-1.5 bg-white/20 text-white text-sm font-semibold rounded-full">
                  {players.filter(p => p.side === "A" && p.isValid).length}/{maxPlayersPerSide}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {slots.map((position) => {
                const player = getPlayerForSlot("A", position);

                return (
                  <div
                    key={`A-${position}`}
                    className={`p-4 rounded-xl border transition-all duration-200 ${player?.isValid
                      ? "ring-2 ring-green-400"
                      : "border-gray-200 hover:border-gray-300"
                      }`}
                    style={{
                      backgroundColor: player?.isValid ? 'rgba(46, 85, 185, 0.05)' : 'white',
                      borderColor: player?.isValid ? '#2E55B9' : '#e5e7eb'
                    }}
                  >
                    {player?.isValid ? (
                      // Player Card
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {player.playerImage ? (
                              <img
                                src={Common.Utility.getImagePath(player.playerImage)}
                                alt={player.playerName}
                                className="w-14 h-14 object-cover rounded-full border-2"
                                style={{ borderColor: '#2E55B9' }}
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0C1B3A 0%, #2E55B9 100%)' }}>
                                <Icon icon="heroicons:user" className="w-7 h-7 text-white" />
                              </div>
                            )}
                            <div>
                              <div className="font-semibold" style={{ color: '#0C1B3A' }}>
                                {player.firstName} {player.lastName}
                              </div>
                              <div className="text-xs" style={{ color: '#6b7280' }}>{player.phoneCode} {player.phone}</div>
                            </div>
                          </div>
                          {player.playerLevel !== null && (
                            <div className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                              {player.playerLevel?.toFixed(2) || "N/A"}
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePlayerFromSlot("A", position)}
                          className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Icon icon="heroicons:trash" className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      // Empty Slot with Add Button
                      <button
                        type="button"
                        onClick={() => openAddPlayerModal("A", position)}
                        className="w-full p-6 rounded-lg border-2 border-dashed transition-all duration-200 hover:border-solid hover:shadow-md"
                        style={{
                          borderColor: '#d1d5db',
                          backgroundColor: '#f9fafb'
                        }}
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#2E55B9' }}>
                            <Icon icon="heroicons:plus" className="w-6 h-6 text-white" />
                          </div>
                          <span className="text-sm font-medium" style={{ color: '#0C1B3A' }}>Position {position}</span>
                          <span className="text-xs" style={{ color: '#6b7280' }}>Click to add player</span>
                        </div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* VS Separator - Center */}
          <div className="hidden lg:flex flex-col items-center justify-center py-8 relative">
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] rounded-full blur-xl opacity-40 animate-pulse"></div>
              <div className="relative w-20 h-20 bg-gradient-to-br from-[#0C1B3A] via-[#1a2a4e] to-[#2E55B9] rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                <span className="text-white font-black text-2xl tracking-wider">VS</span>
              </div>
            </div>
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Battle</p>
            </div>
          </div>

          {/* Side B */}
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 rounded-2xl shadow-lg shadow-purple-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-white font-black text-lg">B</span>
                  </div>
                  <div>
                    <p className="font-bold text-white">Side B</p>
                    <p className="text-purple-200 text-xs">Team Two</p>
                  </div>
                </div>
                <span className="px-3 py-1.5 bg-white/20 text-white text-sm font-semibold rounded-full">
                  {players.filter(p => p.side === "B" && p.isValid).length}/{maxPlayersPerSide}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {slots.map((position) => {
                const player = getPlayerForSlot("B", position);

                return (
                  <div
                    key={`B-${position}`}
                    className={`p-4 rounded-xl border transition-all duration-200 ${player?.isValid
                      ? "ring-2 ring-green-400"
                      : "border-gray-200 hover:border-gray-300"
                      }`}
                    style={{
                      backgroundColor: player?.isValid ? 'rgba(46, 85, 185, 0.05)' : 'white',
                      borderColor: player?.isValid ? '#2E55B9' : '#e5e7eb'
                    }}
                  >
                    {player?.isValid ? (
                      // Player Card
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {player.playerImage ? (
                              <img
                                src={Common.Utility.getImagePath(player.playerImage)}
                                alt={player.playerName}
                                className="w-14 h-14 object-cover rounded-full border-2"
                                style={{ borderColor: '#2E55B9' }}
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0C1B3A 0%, #2E55B9 100%)' }}>
                                <Icon icon="heroicons:user" className="w-7 h-7 text-white" />
                              </div>
                            )}
                            <div>
                              <div className="font-semibold" style={{ color: '#0C1B3A' }}>
                                {player.firstName} {player.lastName}
                              </div>
                              <div className="text-xs" style={{ color: '#6b7280' }}>{player.phoneCode} {player.phone}</div>
                            </div>
                          </div>
                          {player.playerLevel !== null && (
                            <div className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                              {player.playerLevel?.toFixed(2) || "N/A"}
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePlayerFromSlot("B", position)}
                          className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Icon icon="heroicons:trash" className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      // Empty Slot with Add Button
                      <button
                        type="button"
                        onClick={() => openAddPlayerModal("B", position)}
                        className="w-full p-6 rounded-lg border-2 border-dashed transition-all duration-200 hover:border-solid hover:shadow-md"
                        style={{
                          borderColor: '#d1d5db',
                          backgroundColor: '#f9fafb'
                        }}
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#2E55B9' }}>
                            <Icon icon="heroicons:plus" className="w-6 h-6 text-white" />
                          </div>
                          <span className="text-sm font-medium" style={{ color: '#0C1B3A' }}>Position {position}</span>
                          <span className="text-xs" style={{ color: '#6b7280' }}>Click to add player</span>
                        </div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Add Player Modal */}
      <DialogModal
        open={modalOpen}
        setOpen={setModalOpen}
        title={`Add Player - Side ${selectedSlot?.side} Position ${selectedSlot?.position}`}
        className="max-w-md"
        Child={ModalContent}
        Footer={ModalFooter}
      />
    </>
  );
};

const ResultUploadSection = ({ formikProps }) => {
  const [sideASets, setSideASets] = useState(["", "", ""]);
  const [sideBSets, setSideBSets] = useState(["", "", ""]);
  const [winner, setWinner] = useState(null);

  // Get players from the match
  const matchPlayers = formikProps.values.players || [];
  const sideAPlayers = matchPlayers.filter(p => p.side === "A" && p.playerId);
  const sideBPlayers = matchPlayers.filter(p => p.side === "B" && p.playerId);

  const calculateWinner = () => {
    let sideAWins = 0;
    let sideBWins = 0;

    for (let i = 0; i < 3; i++) {
      if (sideASets[i] !== "" && sideBSets[i] !== "") {
        const scoreA = parseInt(sideASets[i]);
        const scoreB = parseInt(sideBSets[i]);
        if (scoreA > scoreB) {
          sideAWins++;
        } else if (scoreA < scoreB) {
          sideBWins++;
        }
      }
    }

    if (sideAWins > sideBWins) {
      setWinner("A");
      formikProps.setFieldValue("side", "A");
    } else if (sideBWins > sideAWins) {
      setWinner("B");
      formikProps.setFieldValue("side", "B");
    } else if (sideAWins === 0 && sideBWins === 0) {
      setWinner(null);
      formikProps.setFieldValue("side", "");
    } else {
      setWinner(null);
    }
  };

  // Initialize result logs structure based on players and sets
  useEffect(() => {
    if (sideAPlayers.length > 0 && sideBPlayers.length > 0) {
      const sets = [1, 2, 3];
      const results = [];

      // Create result logs for Side A players
      sideAPlayers.forEach((player) => {
        sets.forEach((set) => {
          results.push({
            playerId: player.playerId,
            side: "A",
            round: set,
            points: "",
            gameType: 1,
            resultType: 1,
          });
        });
      });

      // Create result logs for Side B players
      sideBPlayers.forEach((player) => {
        sets.forEach((set) => {
          results.push({
            playerId: player.playerId,
            side: "B",
            round: set,
            points: "",
            gameType: 1,
            resultType: 1,
          });
        });
      });

      formikProps.setFieldValue("resultLogs", results);
    }
  }, [sideAPlayers.length, sideBPlayers.length]);

  // Calculate winner based on set scores
  useEffect(() => {
    calculateWinner();
  }, [sideASets, sideBSets]);

  const handleSetChange = (side, setIndex, value) => {
    const numValue = value === "" ? "" : value;

    let updatedSetsA = sideASets;
    let updatedSetsB = sideBSets;

    if (side === "A") {
      const newSets = [...sideASets];
      newSets[setIndex] = numValue;
      setSideASets(newSets);
      updatedSetsA = newSets;
    } else {
      const newSets = [...sideBSets];
      newSets[setIndex] = numValue;
      setSideBSets(newSets);
      updatedSetsB = newSets;
    }

    // Update result logs with new points
    updateResultLogsPoints(updatedSetsA, updatedSetsB);
  };

  const updateResultLogsPoints = (setsA, setsB) => {
    const logs = [...(formikProps.values.resultLogs || [])];

    logs.forEach((log, index) => {
      const setIndex = log.round - 1;
      if (log.side === "A" && setsA[setIndex] !== undefined) {
        log.points = setsA[setIndex] === "" ? 0 : parseInt(setsA[setIndex].toString(), 10) || 0;
      } else if (log.side === "B" && setsB[setIndex] !== undefined) {
        log.points = setsB[setIndex] === "" ? 0 : parseInt(setsB[setIndex].toString(), 10) || 0;
      }
    });

    formikProps.setFieldValue("resultLogs", logs);
  };

  // Get side names
  const getSideAName = () => {
    if (sideAPlayers.length > 0) {
      return `Side A (${sideAPlayers.map(p => p.playerName).join(", ")})`;
    }
    return "Side A";
  };

  const getSideBName = () => {
    if (sideBPlayers.length > 0) {
      return `Side B (${sideBPlayers.map(p => p.playerName).join(", ")})`;
    }
    return "Side B";
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 rounded-2xl p-5 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Icon icon="heroicons:trophy" className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Match Result</h3>
              <p className="text-amber-100 text-sm">Enter scores for each set</p>
            </div>
          </div>
          <span className="px-3 py-1.5 bg-red-500/30 text-white text-sm font-medium rounded-full border border-red-300/50">
            <Icon icon="heroicons:exclamation-triangle" className="w-4 h-4 inline mr-1" />
            Required for Exclusive
          </span>
        </div>
      </div>

      <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <div className="font-semibold text-lg w-1/2">Teams</div>
          <div className="flex justify-between w-1/2">
            <div className="text-center font-semibold">Set 1</div>
            <div className="text-center font-semibold">Set 2</div>
            <div className="text-center font-semibold">Set 3</div>
          </div>
        </div>

        {/* Side A */}
        <div className="flex justify-between items-center mb-4">
          <div className="w-1/2 pr-4">
            <div className="font-medium text-gray-900">{getSideAName()}</div>
          </div>
          <div className="flex justify-between w-1/2 space-x-2">
            {[0, 1, 2].map((index) => (
              <input
                key={`sideA-set-${index}`}
                type="number"
                min="0"
                className="w-16 px-2 py-2 text-center bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all outline-none"
                value={sideASets[index]}
                onChange={(e) => handleSetChange("A", index, e.target.value)}
                placeholder="0"
              />
            ))}
          </div>
        </div>

        {/* Side B */}
        <div className="flex justify-between items-center">
          <div className="w-1/2 pr-4">
            <div className="font-medium text-gray-900">{getSideBName()}</div>
          </div>
          <div className="flex justify-between w-1/2 space-x-2">
            {[0, 1, 2].map((index) => (
              <input
                key={`sideB-set-${index}`}
                type="number"
                min="0"
                className="w-16 px-2 py-2 text-center bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all outline-none"
                value={sideBSets[index]}
                onChange={(e) => handleSetChange("B", index, e.target.value)}
                placeholder="0"
              />
            ))}
          </div>
        </div>

        {winner && (
          <div className="mt-4 text-center font-semibold text-green-600">
            Winner: Side {winner}
          </div>
        )}

        {!winner && (sideASets.some(set => set !== "") || sideBSets.some(set => set !== "")) && (
          <div className="mt-4 text-center font-semibold text-gray-500">
            No Result (Tie or Incomplete)
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateMatch;
